import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from 'react';
import {faAdjust} from '@fortawesome/pro-solid-svg-icons';
import {createTheme, type PaletteMode} from '@mui/material';
import StylesEngineProvider from '@mui/material/StyledEngineProvider';
import {ThemeProvider} from '@mui/material/styles';
import {StylesProvider} from '@mui/styles';
import {useAppCombinedMenuStore} from '@netx/core-app-next/store/appCombinedMenuItems';
import {isBrandPortal} from '@netx/core-cms-next/utils/isBrandPortal';
import {useDebouncedCallback} from '@netx/core-components/hooks/useDebouncedCallback';
import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {useUserPref} from '@netx/core-components/hooks/useUserPref';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {enumToValues} from '@netx/core-next/utils/enum';
import {useUserMenuStore} from '@netx/core-user/store/userMenuItems';
import {getThemeMode, setThemeMode} from '@netx/core/css';
import {NetxThemeMode, NetxThemePref} from './constants';
import {NetxThemeModeLabel, NetxThemeTitle} from './locales';
import type {Theme} from './types';
import {getComponentOverrides} from './utils/getComponentOverrides';
import {getThemeMixins} from './utils/getThemeMixins';
import {getThemePalette} from './utils/getThemePalette';

const isValidNetxThemeMode = (value?: string): value is NetxThemeMode =>
	!!value && enumToValues(NetxThemeMode).includes(value as any);

export const baseTheme = (
	type: PaletteMode = NetxThemeMode.LIGHT,
	demo = false,
) =>
	createTheme(
		{
			mixins: getThemeMixins(type, {demo}),
			palette: getThemePalette(type, {demo}),
			components: getComponentOverrides(type, {demo}),
			typography: {
				// TODO: lets move this to a util - still working some things out but atm
				// we have the portal user fonts reflected here
				fontFamily: isBrandPortal()
					? '"themeFontRegular", "Roboto", "Helvetica", "Arial", sans-serif'
					: undefined,
			},
		},
		[i18n.language],
	);

/** netx theme */
const NetxTheme = ({children}: PropsWithChildren<{}>) => {
	const [theme, setTheme] = useState<Theme>(() => baseTheme(getThemeMode()));

	const [userThemeMode, setUserThemeMode] = useUserPref<false, NetxThemeMode>(
		NetxThemePref.USER_SELECTED,
		{
			parseIn: (value) => {
				if (isValidNetxThemeMode(value)) return value;
				return NetxThemeMode.DEFAULT;
			},
			parseOut: (value) => {
				setThemeMode(value);
				return value;
			},
		},
	);

	const debouncedReloadTheme = useDebouncedCallback(
		() => setTheme(baseTheme(getThemeMode())),
		150,
	);

	useDispatcher(
		'theme:toggled',
		useCallback((themeMode: PaletteMode) => setTheme(baseTheme(themeMode)), []),
	);

	useDispatcher('css:variable:updated', debouncedReloadTheme);

	useEffect(() => {
		// portal does not currently support a dark mode theme
		if (isBrandPortal()) return;

		const items = [
			{
				name: 'theme-mode',
				label: NetxThemeTitle.THEME,
				icon: faAdjust,
				cb: () => {},
				order: 0,
				group: 3,
				children: [
					{
						name: `theme-mode-${NetxThemeMode.DEFAULT}`,
						cb: () => setUserThemeMode(NetxThemeMode.DEFAULT),
						label: NetxThemeModeLabel[NetxThemeMode.DEFAULT],
						selected: userThemeMode === NetxThemeMode.DEFAULT,
					},
					{
						name: `theme-mode-${NetxThemeMode.LIGHT}`,
						cb: () => setUserThemeMode(NetxThemeMode.LIGHT),
						label: NetxThemeModeLabel[NetxThemeMode.LIGHT],
						selected: userThemeMode === NetxThemeMode.LIGHT,
					},
					{
						name: `theme-mode-${NetxThemeMode.DARK}`,
						cb: () => setUserThemeMode(NetxThemeMode.DARK),
						label: NetxThemeModeLabel[NetxThemeMode.DARK],
						selected: userThemeMode === NetxThemeMode.DARK,
					},
				],
			},
		];

		useAppCombinedMenuStore.getState().updateMenuItems(
			items.map((item) => ({...item, order: 3})),
			{
				section: 'user',
				subSection: 'theme',
			},
		);

		useUserMenuStore.getState().updateMenuItems(items, {
			section: 'settings',
			subSection: 'theme',
		});
	}, [theme.palette.mode]);

	useEffect(() => {
		const listener = (lng: string) => setTheme(baseTheme(getThemeMode()));
		i18n.on('languageChanged', listener);
		return () => {
			i18n.off('languageChanged', listener);
		};
	}, []);

	return (
		<StylesProvider>
			<ThemeProvider theme={theme}>
				<StylesEngineProvider injectFirst>{children}</StylesEngineProvider>
			</ThemeProvider>
		</StylesProvider>
	);
};

export default NetxTheme;
